/**
 * PhaseCapabilityCharts — the two phase-plan exhibits.
 *
 * FIRM CAPABILITY (chart 1) draws the question the N-0 adequacy grade cannot
 * answer: per phase, what is left after the largest block trips, against the
 * reserve-padded requirement. The N-0 and N-1 bars sit side by side precisely
 * so the GAP between them is the visible object — a phase can be comfortably
 * adequate on the left bar and short on the right, which is the whole finding.
 *
 * CRITICAL PATH (chart 2) draws each long-lead component as its order window:
 * from the latest defensible order date to the phase COD, with a "today"
 * marker. A bar whose left edge sits behind today is a date that has already
 * become impossible on equipment alone.
 *
 * Both read the assembled series from `buildPhaseCapabilityTimeline`, so they
 * cannot drift from the risk rows derived by `derivePhasingRisks`.
 *
 * Colour is never the only channel: the N-1 bar carries a hatch when the
 * phase hides an exposure, verdict is stated in the tooltip in words, and the
 * requirement is a labelled reference line rather than a third hue. Recharts
 * inside ChartFrame, matching every other report chart, so the paged deck,
 * /print and the server PDF all measure correctly.
 */
import React from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, LabelList, ReferenceLine,
  Tooltip, XAxis, YAxis,
} from 'recharts';

import { ChartFrame } from '../../ui/ChartFrame';
import { roleColor } from '../../../lib/reportRoleColors';
import { resolveReportChartPalette } from '../../../lib/reportChartTheme';
import type { ReportTheme } from '../../../types/reportTheme';
import { timeAxisTicks } from '../../../lib/phaseCapabilityTimeline';
import type {
  PhaseCapabilityPoint,
  PhaseCapabilityTimeline,
  PhaseCriticalPathBar,
} from '../../../lib/phaseCapabilityTimeline';

const fmtMW = (mw: number): string => `${Math.round(mw).toLocaleString('en-US')} MW`;

const fmtMonthYear = (ms: number): string =>
  new Date(ms).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Shared tooltip shell so both charts read identically. */
const TooltipShell: React.FC<{ theme: ReportTheme; title: string; children: React.ReactNode }> = ({
  theme, title, children,
}) => (
  <div
    className="px-2 py-1 text-xs"
    style={{
      background: roleColor(theme, 'subtleBg'),
      border: `1px solid ${roleColor(theme, 'hair')}`,
      maxWidth: 260,
    }}
  >
    <div className="font-bold" style={{ color: roleColor(theme, 'ink') }}>{title}</div>
    <div style={{ color: roleColor(theme, 'muted') }}>{children}</div>
  </div>
);

export const PhaseFirmCapabilityChart: React.FC<{
  theme: ReportTheme;
  timeline: PhaseCapabilityTimeline;
}> = ({ theme, timeline }) => {
  if (timeline.points.length === 0) return null;
  const palette = resolveReportChartPalette(theme);
  const n0Fill = palette[0]!;
  const n1Fill = palette[1]!;
  const axisInk = roleColor(theme, 'muted');
  const gridStroke = roleColor(theme, 'hair');

  // One reference line per distinct requirement keeps a single y-axis (a second
  // axis for the same unit would be the classic dual-axis mistake).
  const requirements = [...new Set(timeline.points.map((p) => p.requiredFirmMW))]
    .filter((mw) => mw > 0);

  return (
    <div
      className="w-full"
      data-testid="phase-firm-capability-chart"
      data-export-capture=""
      data-export-id="phase-firm-capability-chart"
      data-export-title="Firm capability by phase"
    >
      <ChartFrame
        exportId="phase-firm-capability-chart"
        traceName="report:phase-firm-capability"
        minWidth={100}
        aspect={0.42}
        minHeight={200}
        maxHeight={320}
      >
        <BarChart data={timeline.points} margin={{ top: 16, right: 20, bottom: 8, left: 8 }}>
          <defs>
            {/* Texture channel: an exposed phase stays distinguishable in
                greyscale, print and for a colour-blind reader. */}
            <pattern id="phase-n1-exposed" patternUnits="userSpaceOnUse" width={6} height={6}>
              <rect width={6} height={6} fill={n1Fill} />
              <path d="M0,6 l6,-6" stroke={roleColor(theme, 'ink')} strokeWidth={1.1} opacity={0.55} />
            </pattern>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={gridStroke} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: axisInk }}
            stroke={roleColor(theme, 'rule')}
          />
          <YAxis
            tick={{ fontSize: 11, fill: axisInk }}
            stroke={roleColor(theme, 'rule')}
            width={54}
            tickFormatter={(v: number) => `${Math.round(v)}`}
            label={{
              value: 'MW firm',
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 10, fill: axisInk },
            }}
          />
          {requirements.map((mw) => (
            <ReferenceLine
              key={mw}
              y={mw}
              stroke={roleColor(theme, 'ink')}
              strokeDasharray="5 3"
              strokeWidth={1.3}
              label={{
                value: `Required firm ${fmtMW(mw)} (+15% reserve)`,
                position: 'insideTopRight',
                style: { fontSize: 9, fill: roleColor(theme, 'ink') },
              }}
            />
          ))}
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ payload }) => {
              const p = payload?.[0]?.payload as PhaseCapabilityPoint | undefined;
              if (!p) return null;
              return (
                <TooltipShell theme={theme} title={p.label}>
                  <div>Firm, all running: {fmtMW(p.firmN0MW)}</div>
                  <div>Firm after N&ndash;1: {fmtMW(p.firmN1MW)}</div>
                  <div>Requirement: {fmtMW(p.requiredFirmMW)}</div>
                  <div>
                    {p.n1MarginMW >= 0
                      ? `Survives N-1 with ${fmtMW(p.n1MarginMW)} to spare.`
                      : `Short of the requirement by ${fmtMW(Math.abs(p.n1MarginMW))} at N-1.`}
                  </div>
                  {p.hiddenExposure && <div>Adequate with everything running, so this is not visible in the N-0 check.</div>}
                  {p.assumesWorstCase && <div>Block counts assumed (worst case, one block per source).</div>}
                </TooltipShell>
              );
            }}
          />
          <Bar dataKey="firmN0MW" name="Firm, all running" fill={n0Fill} barSize={22}
               radius={[2, 2, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="firmN1MW" name="Firm after N-1" barSize={22} radius={[2, 2, 0, 0]}
               isAnimationActive={false}>
            {timeline.points.map((p) => (
              <Cell key={p.phaseId} fill={p.hiddenExposure ? 'url(#phase-n1-exposed)' : n1Fill} />
            ))}
            <LabelList
              dataKey="firmN1MW"
              position="top"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- recharts LabelFormatter is loosely typed; matches ThermoAnalysisView's established call
              formatter={(v: any) => String(Math.round(Number(v)))}
              style={{ fontSize: 9, fill: roleColor(theme, 'muted') }}
            />
          </Bar>
        </BarChart>
      </ChartFrame>
      {/* Legend lives outside the plot so identity never depends on colour
          alone, and reads the same in the paged deck. */}
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]"
           style={{ color: roleColor(theme, 'muted') }}>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, background: n0Fill, display: 'inline-block' }} />
          Firm, all running (N&ndash;0)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, background: n1Fill, display: 'inline-block' }} />
          Firm after loss of largest block (N&ndash;1)
        </span>
        {timeline.anyHiddenExposure && (
          <span className="inline-flex items-center gap-1.5">
            <span style={{
              width: 10, height: 10, display: 'inline-block', background: n1Fill,
              backgroundImage: `linear-gradient(45deg, ${roleColor(theme, 'ink')} 25%, transparent 25%, transparent 50%, ${roleColor(theme, 'ink')} 50%, ${roleColor(theme, 'ink')} 75%, transparent 75%)`,
              backgroundSize: '5px 5px',
            }} />
            Hatched: adequate at N&ndash;0, short at N&ndash;1
          </span>
        )}
      </div>
    </div>
  );
};

export const PhaseCriticalPathChart: React.FC<{
  theme: ReportTheme;
  timeline: PhaseCapabilityTimeline;
}> = ({ theme, timeline }) => {
  if (timeline.bars.length === 0) return null;
  const axisInk = roleColor(theme, 'muted');
  const okFill = roleColor(theme, 'accent');
  const tightFill = roleColor(theme, 'warning');
  const lateFill = roleColor(theme, 'negative');

  // Recharts has no Gantt primitive: stack a transparent offset bar under the
  // visible duration bar so each row starts at its own order date.
  const rows = timeline.bars.map((bar) => ({
    ...bar,
    offset: bar.orderByMs,
    duration: Math.max(bar.codMs - bar.orderByMs, 1),
  }));
  const minMs = Math.min(...rows.map((r) => r.offset), timeline.nowMs);
  const maxMs = Math.max(...rows.map((r) => r.offset + r.duration), timeline.nowMs);
  // A single bar whose order date IS its COD would give a zero-width domain, and
  // Recharts renders a degenerate numeric domain as an epoch-anchored axis. Floor
  // the padding at ~2 weeks so the window is always visible.
  const pad = Math.max((maxMs - minMs) * 0.04, 12 * 24 * 60 * 60 * 1000);
  const ticks = timeAxisTicks(minMs - pad, maxMs + pad);

  const fillFor = (verdict: PhaseCriticalPathBar['verdict']): string =>
    verdict === 'infeasible' ? lateFill : verdict === 'tight' ? tightFill : okFill;

  return (
    <div
      className="w-full"
      data-testid="phase-critical-path-chart"
      data-export-capture=""
      data-export-id="phase-critical-path-chart"
      data-export-title="Long-lead order windows"
    >
      <ChartFrame
        exportId="phase-critical-path-chart"
        traceName="report:phase-critical-path"
        minWidth={100}
        aspect={0.34}
        minHeight={160}
        maxHeight={300}
      >
        <ComposedChart layout="vertical" data={rows} margin={{ top: 12, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke={roleColor(theme, 'hair')} />
          <XAxis
            type="number"
            domain={[minMs - pad, maxMs + pad]}
            /*
             * REQUIRED, not cosmetic. This Gantt is a STACKED bar: a transparent
             * `offset` spacer spans [0, orderByMs] with the visible `duration`
             * stacked on it. Recharts defaults `allowDataOverflow` to false,
             * which EXPANDS the axis to fit all data — and the spacer's data
             * reaches down to 0. So the explicit domain above was silently
             * overridden and the axis ran from the Unix epoch: a live chart
             * showing two 2028 order windows drew its scale from "Jan 1970",
             * compressing both bars into the last 4% of the plot.
             *
             * With overflow allowed the domain is honoured and the spacer is
             * clipped — it is transparent, so nothing visible is lost, and the
             * duration bars start at orderByMs >= minMs and are never clipped.
             */
            allowDataOverflow
            {...(ticks.length > 0 ? { ticks } : {})}
            tickFormatter={(v: number) => fmtMonthYear(v)}
            tick={{ fontSize: 10, fill: axisInk }}
            stroke={roleColor(theme, 'rule')}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 10, fill: axisInk }}
            stroke={roleColor(theme, 'rule')}
            width={132}
          />
          <ReferenceLine
            x={timeline.nowMs}
            stroke={roleColor(theme, 'ink')}
            strokeDasharray="4 3"
            strokeWidth={1.3}
            label={{
              value: 'today',
              position: 'top',
              style: { fontSize: 9, fill: roleColor(theme, 'ink') },
            }}
          />
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ payload }) => {
              const r = payload?.[0]?.payload as (typeof rows)[number] | undefined;
              if (!r) return null;
              return (
                <TooltipShell theme={theme} title={r.label}>
                  <div>Order by {fmtMonthYear(r.orderByMs)} for a {fmtMonthYear(r.codMs)} COD</div>
                  <div>Lead time {r.leadTimeMonthsLow}&ndash;{r.leadTimeMonthsHigh} months</div>
                  <div>
                    {r.verdict === 'infeasible'
                      ? 'The order window has already passed.'
                      : r.verdict === 'tight'
                        ? 'The order window closes within six months.'
                        : 'The order window is open.'}
                  </div>
                  {r.proxyBaseline && <div>Nearest-class lead-time proxy; confirm with the OEM.</div>}
                </TooltipShell>
              );
            }}
          />
          {/* Transparent spacer: positions each duration bar at its order date. */}
          <Bar dataKey="offset" stackId="gantt" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="duration" stackId="gantt" barSize={13} radius={[2, 2, 2, 2]} isAnimationActive={false}>
            {rows.map((r) => (
              <Cell key={`${r.phaseId}-${r.label}`} fill={fillFor(r.verdict)} />
            ))}
          </Bar>
        </ComposedChart>
      </ChartFrame>
      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px]"
           style={{ color: roleColor(theme, 'muted') }}>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, background: okFill, display: 'inline-block' }} />
          Order window open
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, background: tightFill, display: 'inline-block' }} />
          Closes within 6 months
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 10, height: 10, background: lateFill, display: 'inline-block' }} />
          Already passed
        </span>
        <span>Bar spans the latest defensible order date to the phase COD. Equipment delivery only.</span>
      </div>
    </div>
  );
};
